import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { Users, PartyPopper, Link2, Settings as SettingsIcon, Copy, MessageCircle } from "lucide-react";
import { updateEvent, checkEventLimit } from "../../services/createEventService";
import { getEventById } from "../../services/eventService";
import { getSocialLinks } from "../../services/roomService";
import Button from "../common/Button";
import Card from "../common/Card";
import Modal from "../common/Modal";
import { color, radius } from "../../styles/theme";

/**
 * @Room home header — ported from mobile's ARoomPromoter screen
 * (src/Screens/a-room-promoter): editable title/description, stats,
 * share QR/link, and a read-only social links row. Consolidated onto the
 * existing RoomGalleries.jsx page instead of a separate route — see the
 * approved plan.
 */
const RoomHomeHeader = ({ room, eventId, onUpdated }) => {
  const { t } = useTranslation(["rooms", "common"]);
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [description, setDescription] = useState(room?.Description || "");
  const [saving, setSaving] = useState(false);
  const [showShare, setShowShare] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [links, setLinks] = useState([]);

  useEffect(() => {
    setDescription(room?.Description || "");
  }, [room?.Description]);

  useEffect(() => {
    getSocialLinks()
      .then((res) => {
        const list = Array.isArray(res.data) ? res.data : res.data?.Result || [];
        setLinks(list.filter((l) => l?.platformCode && l?.Url));
      })
      .catch(() => {});
  }, []);

  const handleSaveTitle = async () => {
    setSaving(true);
    try {
      // PutEventAsync (EventCrudService.cs) assigns a couple of fields
      // (IsWatermark, IsJoinRequestDisabled) straight from the request body
      // with no fallback to the existing value if omitted — a minimal
      // { description } payload was silently resetting both of those to
      // false on every description save. Round-trip the full current event
      // instead, only overriding Description.
      const res = await getEventById(eventId);
      const currentEvent = res.data?.Result || res.data;
      await updateEvent(eventId, { ...currentEvent, Description: description });
      toast.success(t("roomHomeHeader.toasts.titleUpdated"));
      setEditing(false);
      onUpdated?.(description);
    } catch (err) {
      toast.error(err.response?.data?.Message || t("roomHomeHeader.toasts.titleUpdateFailed"));
    } finally {
      setSaving(false);
    }
  };

  const handleShare = async () => {
    setSharing(true);
    try {
      const res = await checkEventLimit("Public");
      if (res.data?.Result === "BusinessNotVerified") {
        toast.info(t("roomHomeHeader.toasts.verifyBusiness"));
        navigate("/settings");
        return;
      }
      setShowShare(true);
    } catch {
      toast.error(t("roomHomeHeader.toasts.shareCheckFailed"));
    } finally {
      setSharing(false);
    }
  };

  const copyLink = () => {
    if (!room?.ChatRoomLink) return;
    navigator.clipboard.writeText(room.ChatRoomLink).then(() => toast.success(t("roomHomeHeader.toasts.linkCopied")));
  };

  return (
    <Card style={s.card}>
      <div style={s.topRow}>
        {room?.LogoUrl ? (
          <img src={room.LogoUrl.replace("\\", "/")} alt={room?.Name} style={s.avatar} onError={(e) => { e.target.style.display = "none"; }} />
        ) : (
          <div style={s.avatarFallback}>{room?.Name?.charAt(0).toUpperCase() || "R"}</div>
        )}

        <div style={{ flex: 1, minWidth: 0 }}>
          <h2 style={s.name}>{room?.Name}</h2>

          {editing ? (
            <div style={{ marginTop: 8 }}>
              <textarea
                className="input"
                style={s.textarea}
                rows={3}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={t("roomHomeHeader.descriptionPlaceholder")}
              />
              <div style={s.editActions}>
                <Button onClick={handleSaveTitle} disabled={saving} size="sm">{saving ? t("roomHomeHeader.saving") : t("common:actions.save")}</Button>
                <Button variant="secondary" size="sm" onClick={() => { setEditing(false); setDescription(room?.Description || ""); }} disabled={saving}>{t("common:actions.cancel")}</Button>
              </div>
            </div>
          ) : (
            <p style={s.description} onClick={() => setEditing(true)} title={t("roomHomeHeader.clickToEditTitle")}>
              {room?.Description || <span style={s.descPlaceholder}>{t("roomHomeHeader.addDescriptionPlaceholder")}</span>}
            </p>
          )}
        </div>
      </div>

      <div style={s.statsRow}>
        {/* Mobile's ARoomPromoter screen sends these to its Participants and
            EventList screens respectively (onParticipant/onEnterEvents) —
            Participants already exists here as the Event Details Participants
            tab (same eventId, since @Room chat room id === event id); Total
            Events is mobile's EventList filtered to byUserEvents (events
            this promoter actually created, not ones they're a sub-promoter
            on) — same distinction via My Events' own Role filter here. */}
        <button
          type="button"
          className="row-link"
          style={s.statBox}
          onClick={() => navigate(`/events/${eventId}`, { state: { initialTab: "Participants", from: "room", roomId: eventId, room } })}
        >
          <span style={s.statValue}><Users size={13} strokeWidth={2.25} /> {room?.TotalUsers ?? 0}</span>
          <span style={s.statLabel}>{t("roomHomeHeader.stats.joinedUsers")}</span>
        </button>
        <button type="button" className="row-link" style={s.statBox} onClick={() => navigate("/events", { state: { roleFilter: "promoter" } })}>
          <span style={s.statValue}><PartyPopper size={13} strokeWidth={2.25} /> {room?.TotalEvents ?? 0}</span>
          <span style={s.statLabel}>{t("roomHomeHeader.stats.totalEvents")}</span>
        </button>
      </div>

      <div style={s.actionsRow}>
        <Button size="sm" icon={Link2} onClick={handleShare} disabled={sharing}>{sharing ? t("roomHomeHeader.checkingButton") : t("roomHomeHeader.shareButton")}</Button>
        {/* Chat room id === event id (@Room chat is just an event with
            EventType === "ARoom") — same route ChatThreadPage.jsx already
            serves for regular events. */}
        <Button size="sm" variant="secondary" icon={MessageCircle} onClick={() => navigate(`/chat/${eventId}`)}>{t("roomHomeHeader.chatButton")}</Button>
        <Button size="sm" variant="secondary" icon={SettingsIcon} onClick={() => navigate(`/rooms/${eventId}/settings`, { state: { room } })}>{t("roomHomeHeader.settingsButton")}</Button>
      </div>

      {links.length > 0 && (
        <div style={s.socialRow}>
          {links.map((l) => (
            <a key={l.platformCode} href={l.Url} target="_blank" rel="noreferrer" style={s.socialLink}>
              {t(`roomHomeHeader.socialPlatforms.${l.platformCode}`, { defaultValue: l.platformCode })}
            </a>
          ))}
        </div>
      )}

      {showShare && (
        <Modal title={t("roomHomeHeader.shareModal.title")} onClose={() => setShowShare(false)} maxWidth={340}>
          <div style={{ textAlign: "center" }}>
            {room?.QrCodeBase64 ? (
              <img
                src={room.QrCodeBase64.startsWith("data:") ? room.QrCodeBase64 : `data:image/png;base64,${room.QrCodeBase64}`}
                alt={t("roomHomeHeader.shareModal.qrAlt")}
                style={s.qrImg}
              />
            ) : (
              <p style={s.noQr}>{t("roomHomeHeader.shareModal.noQr")}</p>
            )}
            {room?.ChatRoomLink && (
              <div style={s.linkRow}>
                <input readOnly value={room.ChatRoomLink} className="input" style={s.linkInput} onFocus={(e) => e.target.select()} />
                <Button size="sm" icon={Copy} onClick={copyLink}>{t("common:actions.copy")}</Button>
              </div>
            )}
          </div>
        </Modal>
      )}
    </Card>
  );
};

const s = {
  card: { padding: "20px 24px", marginBottom: 28, maxWidth: 640 },
  topRow: { display: "flex", gap: 16, alignItems: "flex-start" },
  avatar: { width: 56, height: 56, borderRadius: "50%", objectFit: "cover", border: `2px solid ${color.primary100}`, flexShrink: 0 },
  avatarFallback: {
    width: 56, height: 56, borderRadius: "50%", background: color.primary500, color: "#fff",
    display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 22, flexShrink: 0,
  },
  name: { margin: "0 0 4px", fontSize: 18, fontWeight: 800, color: color.textPrimary, letterSpacing: -0.3 },
  description: { margin: 0, fontSize: 13, color: color.gray700, cursor: "pointer", lineHeight: 1.5 },
  descPlaceholder: { color: color.textMuted, fontStyle: "italic" },
  textarea: { width: "100%", resize: "vertical", fontFamily: "inherit" },
  editActions: { display: "flex", gap: 8, marginTop: 8 },
  statsRow: { display: "flex", gap: 24, marginTop: 18, fontSize: 13, color: color.gray800 },
  statBox: {
    display: "flex", flexDirection: "column", gap: 3, fontWeight: 700,
    background: "none", border: "none", padding: "4px 8px", margin: "-4px -8px", borderRadius: radius.sm,
    textAlign: "left", cursor: "pointer", font: "inherit", color: "inherit",
  },
  statValue: { display: "inline-flex", alignItems: "center", gap: 6 },
  statLabel: { fontSize: 11, color: color.textMuted, fontWeight: 500 },
  actionsRow: { display: "flex", gap: 10, marginTop: 18 },
  socialRow: { display: "flex", flexWrap: "wrap", gap: 10, marginTop: 18, paddingTop: 16, borderTop: `1px solid ${color.gray100}` },
  socialLink: {
    fontSize: 12.5, color: color.primary600, textDecoration: "none", fontWeight: 600,
    background: color.primary50, padding: "6px 12px", borderRadius: radius.pill,
  },
  qrImg: { width: 200, height: 200, objectFit: "contain", margin: "0 auto 16px" },
  noQr: { fontSize: 13, color: color.textMuted, marginBottom: 16 },
  linkRow: { display: "flex", gap: 8 },
  linkInput: { flex: 1, minWidth: 0, fontSize: 11.5, color: color.textSecondary },
};

export default RoomHomeHeader;
